// PWS section reference chips — renders a pws_sections column value (TEXT
// holding a JSON []string, the program_tasks/optional_tasks convention) as
// small badges. Empty input renders nothing; non-array/legacy values render
// as one bare chip so stored data is never invisible (an invisible value is
// what let the edit form wipe it).
//
// ExpandablePwsChips is the CLIN-row variant: collapsed to the first section
// with a "+N more" toggle, and each section deep-links into the source
// document via its own PwsObligation provenance ref.
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraBadge, specraBadgeVariants } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useMemo, useState } from 'react';
import { DocumentViewerDrawer } from '../../../../components/ProposalDashboard/DocumentViewer';
import { useSourceRefsForPrefix } from '../hooks/lineageAuditHooks';
import { parseSpan } from '../lib/sourceSpan';
export function parsePwsSections(raw) {
    if (!raw || raw.trim() === '') return [];
    try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
            const sections = parsed.filter((s)=>typeof s === 'string' && s.trim() !== '');
            return [
                ...new Set(sections.map((s)=>s.trim()))
            ];
        }
    // Scalar JSON falls through: a legacy bare "5.1" parses as a NUMBER, and
    // treating it as empty here made the edit form seed blank and then wipe
    // the stored value on save. Preserve it as one bare section instead.
    } catch  {
    // Not JSON at all — legacy rows hold a bare section string.
    }
    return [
        raw.trim()
    ];
}
export function PwsChips({ sections }) {
    const refs = parsePwsSections(sections);
    if (refs.length === 0) return null;
    return /*#__PURE__*/ _jsx("span", {
        className: "inline-flex flex-wrap gap-1",
        children: refs.map((r)=>/*#__PURE__*/ _jsx(SpecraBadge, {
                variant: "outline",
                className: "font-mono text-[10px]",
                "data-testid": Testids.deliveryShared.pwsChip,
                title: `PWS ${r}`,
                children: r
            }, r))
    });
}
// Resolves each stored PWS section string to its PwsObligation provenance ref
// (or nothing, if extraction hasn't landed one). Extracted out of
// ExpandablePwsChips so OptionalTaskExercisesDrawer can ask the same question
// — "did any of this task's PWS sections resolve a real citation?" — without
// re-deriving PwsSectionChip's canOpen logic by hand. The react-query key
// (['source-refs-prefix', 'PwsObligation', `${programId}/`]) is identical for
// every caller sharing a programId, so a second consumer on the same drawer
// costs a cache hit, not a second fetch.
export function usePwsSectionRefs({ sections, programId }) {
    const all = parsePwsSections(sections);
    // ONE prefix query per program, shared by every chip list on the surface (a
    // CLINs tab renders hundreds of rows) — resolved into a Map once so each
    // chip is a hash lookup, not a scan over up to 2000 refs per render. A ref
    // that fell past the server's prefix cap renders as an inert badge,
    // indistinguishable from "no provenance" — the server logs when that
    // happens; at ~400 refs per real program the cap is far away.
    // pollWhileEmpty: this only ever renders inside OptionalTaskExercisesDrawer
    // (SAD-339), which can already be open on a program whose PWS provenance
    // extraction hasn't landed yet — see the matching comment on
    // ContractSourceLineItem's useSourceRefsForTarget call.
    const { data: programRefs } = useSourceRefsForPrefix({
        targetObjectType: 'PwsObligation',
        targetObjectIdPrefix: `${programId}/`,
        enabled: all.length > 0,
        pollWhileEmpty: true
    });
    return useMemo(()=>{
        const prefix = `${programId}/`;
        const m = new Map();
        for (const r of programRefs ?? []){
            if (!r.targetObjectId.startsWith(prefix)) continue;
            const section = r.targetObjectId.slice(prefix.length);
            if (!m.has(section)) m.set(section, r);
        }
        return m;
    }, [
        programRefs,
        programId
    ]);
}
export function ExpandablePwsChips({ sections, programId, projectId }) {
    const all = parsePwsSections(sections);
    const [expanded, setExpanded] = useState(false);
    const refBySection = usePwsSectionRefs({
        sections,
        programId
    });
    if (all.length === 0) return null;
    const shown = expanded ? all : all.slice(0, 1);
    return /*#__PURE__*/ _jsxs("span", {
        className: "inline-flex flex-wrap items-center gap-1",
        children: [
            shown.map((s)=>/*#__PURE__*/ _jsx(PwsSectionChip, {
                    section: s,
                    sectionRef: refBySection.get(s),
                    projectId: projectId
                }, s)),
            all.length > 1 && /*#__PURE__*/ _jsx("button", {
                type: "button",
                className: "text-[10px] text-muted-foreground underline-offset-2 hover:underline",
                // stopPropagation: the chips render inside clickable rows (CLIN row
                // opens its detail drawer) — expanding must not also fire the row.
                onClick: (e)=>{
                    e.stopPropagation();
                    setExpanded((cur)=>!cur);
                },
                "data-testid": Testids.deliveryShared.pwsChipMore,
                children: expanded ? 'less' : `+${all.length - 1} more`
            })
        ]
    });
}
// One section chip. Extraction records per-section provenance as
// "PwsObligation"/"{programId}/{section}" (reverie program-scopes the natural
// key); the parent resolves the ref from its shared prefix fetch. Without one
// the chip stays a plain badge rather than a dead-end click.
function PwsSectionChip({ section, sectionRef, projectId }) {
    const [viewerOpen, setViewerOpen] = useState(false);
    const ref = sectionRef;
    const canOpen = !!projectId && !!ref?.sourceArtifactId;
    if (!canOpen) {
        return /*#__PURE__*/ _jsx(SpecraBadge, {
            variant: "outline",
            className: "font-mono text-[10px]",
            "data-testid": Testids.deliveryShared.pwsChip,
            title: `PWS ${section}`,
            children: section
        });
    }
    const span = parseSpan(ref.sourceSpan);
    const openPage = ref.sourcePage > 0 ? ref.sourcePage : span.page ?? 1;
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx("button", {
                type: "button",
                className: `${specraBadgeVariants({
                    variant: 'outline'
                })} cursor-pointer font-mono text-[10px] hover:bg-muted`,
                "data-testid": Testids.deliveryShared.pwsChip,
                title: `Open PWS ${section} in the source document`,
                onClick: (e)=>{
                    e.stopPropagation();
                    setViewerOpen(true);
                },
                "aria-label": `Open PWS ${section} in the source document`,
                children: section
            }),
            viewerOpen && /*#__PURE__*/ _jsx("span", {
                onClick: (e)=>e.stopPropagation(),
                children: /*#__PURE__*/ _jsx(DocumentViewerDrawer, {
                    isOpen: true,
                    onClose: ()=>setViewerOpen(false),
                    projectId: projectId,
                    document: {
                        documentId: ref.sourceArtifactId,
                        filename: `PWS ${section}`
                    },
                    initialPage: openPage,
                    highlightBlockId: span.blockId,
                    // ExpandablePwsChips only ever renders inside
                    // OptionalTaskExercisesDrawer's SpecraOverlayDrawer (a Radix
                    // dialog at the same z-50 a standalone viewer uses), so its close
                    // button loses hit-testing to the parent's header without
                    // extension mode — see the matching comment on
                    // ContractSourceLineItem's DocumentViewerDrawer in that file.
                    mode: "extension",
                    parentDrawerWidth: 512
                })
            })
        ]
    });
}
